import React, { Suspense, lazy, useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./Newsite/styles/fonts.css";
import "./Newsite/styles/tailwind.generated.css";
import "./Newsite/styles/newsite-base.css";

import Layout from "./Layout";
import { usePageTracking } from "./hooks/useAnalytics";
import Loading from "./utils/Loading";
import ScrollToTop from "./utils/ScrollToTop";

/**
 * App router
 *
 * Only approved, canonical routes remain live in this file.
 * Anything not listed here has been intentionally removed.
 */

// Non-critical UI widgets
const FloatingScrollToTop = lazy(() => import("./components/FloatingScrollToTop"));
const FuturisticChatbot = lazy(() => import("./components/FuturisticChatbot"));

// Core site pages
const NewsiteHome = lazy(() => import("./Newsite/Home/Home"));
const AboutTPC = lazy(() => import("./aboutPage/AboutTPC"));
const PricingModel = lazy(() => import("./pricingPage/AllPricingModels"));
const ProgramsOverview = lazy(() => import("./programs/programsOverview/ProgramsOverview"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));

// Pathway / qualification overview pages
// const AIDeveloperOverview = lazy(() => import("./programs/AIDev/AIDev"));
const AIDeveloperQualification = lazy(() =>
  import("./programs/aiDeveloperQualification/aiDeveloperQualificationStarter"),
);
const SoftwareDeveloperQualification = lazy(() =>
  import("./programs/softwareDeveloperQualification/SoftwareDeveloperQualification"),
);
const EarlyChildhoodDevelopmentPricing = lazy(() =>
  import("./pricingPage/EarlyChildhoodDevelopmentPricing"),
);

// Individual program pages
const JuniorAIDeveloper = lazy(() => import("./programs/juniorAiDeveloper/JuniorAIDeveloper"));
const JuniorSoftwareDeveloper = lazy(() =>
  import("./programs/juniorSoftwareDeveloper/JuniorSoftwareDeveloper"),
);
const PythonProgrammer = lazy(() => import("./programs/pythonProgrammerSP/PythonProgrammerSP"));
const PythonProgrammerSoftwareDeveloper = lazy(() =>
  import("./programs/pythonProgrammerSP/PythonSoftwareDeveloper"),
);
const MachineLearningFoundations = lazy(() =>
  import("./programs/machineLearningFoundations/MachineLearningFoundations"),
);
const AppliedMLTensorflowCV = lazy(() =>
  import("./programs/appliedMLTensorFlow/AppliedMLTensorflowCV"),
);
const DeepLearningNLPMLOps = lazy(() =>
  import("./programs/deepLearningNLPMlops/DeepLearningNLPMLOps"),
);
const WilEisaReadiness = lazy(() =>
  import("./programs/wilEisaReadiness/WilEisaReadiness"),
);
const AIDeveloperBootcamp = lazy(() =>
  import("./programs/aiDeveloperBootcamp/AIDeveloperBootcamp"),
);
const WebFoundations = lazy(() => import("./programs/fullStackWebDev/WebFoundations"));
const JavaScriptProgrammer = lazy(() =>
  import("./programs/softwareEngineeringWithJavaScript/JavaScriptProgrammer"),
);
const SoftwareDevelopmentPractice = lazy(() =>
  import("./programs/softwareDeveloperTrack/SoftwareDevelopmentPractice"),
);
const CapstoneWorkExperienceEisaPrep = lazy(() =>
  import("./programs/softwareDevCapstone/CapstoneWorkExperienceEisaPrep"),
);
const DataScienceWithPython = lazy(() =>
  import("./programs/dataScienceWithPython/DataScienceWithPython"),
);
const AppliedPythonGUI = lazy(() =>
  import("./programs/appliedPythonGUI/AppliedPythonGUI"),
);
const CyberSecurity = lazy(() => import("./programs/cyberSecurity/CyberSecurity"));
const AIProductivity = lazy(() => import("./programs/aiProductivity/AIProductivity"));
const AIProductivityPython = lazy(() =>
  import("./programs/aiProductivityPython/AIProductivityPython"),
);
const AITools = lazy(() => import("./programs/AITools/AITools"));
const ExclusiveOffersSignup = lazy(() => import("./pages/ExclusiveOffersSignup"));
const PreRegistrationSignup = lazy(() => import("./pages/PreRegistrationSignup"));
const BursaryApplicationForm = lazy(() => import("./pages/BursaryApplicationForm"));
const UnityInstitutionEnquiryForm = lazy(() =>
  import("./pages/UnityInstitutionEnquiryForm"),
);

const FullStackWebDeveloperBootcamp = lazy(() =>
  import("./programs/fullStackWebDeveloperBootcamp/FullStackWebDeveloperBootcamp"),
);
const AIForProfessionalsBootcamp = lazy(() =>
  import("./programs/aiForProfessionalsBootcamp/AIForProfessionalsBootcamp"),
);
const PythonDeveloperBootcamp = lazy(() =>
  import("./programs/pythonDeveloperBootcamp/PythonDeveloperBootcamp"),
);

const WIDGET_DELAY_MS = 2000;
const EXCLUSIVE_OFFERS_ROUTE = "/exclusive-offers/x7n3m9";
const PRE_REGISTRATION_ROUTE = "/pre-registration/x7n3m9";
const BURSARY_APPLICATION_ROUTE = "/bursary-application/x7n3m9";
const UNITY_INSTITUTION_ENQUIRY_ROUTE = "/unity-institution-enquiry/x7n3m9";

/**
 * Top-level site routes
 */
const CORE_ROUTES = [
  { path: "/about", Component: AboutTPC },
  { path: "/pricing", Component: PricingModel },
  { path: "/programs", Component: ProgramsOverview },
  { path: "/terms-and-conditions", Component: TermsAndConditions },
  {
  path: "/pricing/early-childhood-development",
  Component: EarlyChildhoodDevelopmentPricing,
},
];

/**
 * Program overview / qualification routes
 */
const PATHWAY_ROUTES = [
  // { path: "/programs/AIDeveloper", Component: AIDeveloperOverview },
  { path: "/programs/ai-developer-qualification", Component: AIDeveloperQualification },
  {
    path: "/programs/software-developer-qualification",
    Component: SoftwareDeveloperQualification,
  },
];

/**
 * Individual program / product routes
 * Source of truth: the program cards component shared above.
 */
const PROGRAM_ROUTES = [
  { path: "/programs/junior-ai-developer", Component: JuniorAIDeveloper },
  { path: "/programs/junior-software-developer", Component: JuniorSoftwareDeveloper },
  { path: "/programs/python-programmer", Component: PythonProgrammer },
  {
    path: "/programs/python-programmer-software-developer",
    Component: PythonProgrammerSoftwareDeveloper,
  },
  { path: "/programs/machine-learning-foundations", Component: MachineLearningFoundations },
  {
    path: "/programs/applied-ml-tensorflow-computer-vision",
    Component: AppliedMLTensorflowCV,
  },
  { path: "/programs/deep-learning-nlp-mlops", Component: DeepLearningNLPMLOps },
  { path: "/programs/wil-eisa-readiness", Component: WilEisaReadiness },
  { path: "/programs/web-foundations", Component: WebFoundations },
  { path: "/programs/javascript-programmer", Component: JavaScriptProgrammer },
  {
    path: "/programs/software-developer-practice",
    Component: SoftwareDevelopmentPractice,
  },
  {
    path: "/programs/capstone-work-experience-eisa-prep",
    Component: CapstoneWorkExperienceEisaPrep,
  },
  { path: "/programs/data-science-python", Component: DataScienceWithPython },
  { path: "/programs/applied-python-gui", Component: AppliedPythonGUI },
  { path: "/programs/cyber-security", Component: CyberSecurity },
  { path: "/programs/ai-productivity", Component: AIProductivity },
  { path: "/programs/ai-productivity-python", Component: AIProductivityPython },
  { path: "/programs/AITools", Component: AITools },
  { path: "/programs/ai-developer-bootcamp", Component: AIDeveloperBootcamp },
  {
    path: "/programs/full-stack-web-developer-bootcamp",
    Component: FullStackWebDeveloperBootcamp,
  },
  {
    path: "/programs/ai-for-professionals-bootcamp",
    Component: AIForProfessionalsBootcamp,
  },
  { path: "/programs/software-developer-with-python-bootcamp", Component: PythonDeveloperBootcamp },
];

const STANDALONE_ROUTES = [
  { path: EXCLUSIVE_OFFERS_ROUTE, Component: ExclusiveOffersSignup },
  { path: PRE_REGISTRATION_ROUTE, Component: PreRegistrationSignup },
  { path: BURSARY_APPLICATION_ROUTE, Component: BursaryApplicationForm },
  {
    path: UNITY_INSTITUTION_ENQUIRY_ROUTE,
    Component: UnityInstitutionEnquiryForm,
  },
];

const NEWSITE_ROUTES = [
  "/",
  "/courses",
  "/courses/:courseSlug",
  "/meet-the-team",
  "/learning-process",
  "/contact-us",
  "/blog",
  "/blog/:postSlug",
  "/privacy-policy",
  "/terms-of-use",
  "/cookie-policy",
];

const renderRoutes = (routes) =>
  routes.map(({ path, Component, props }) => (
    <Route key={path} path={path} element={<Component {...props} />} />
  ));

function App() {
  usePageTracking();
  const location = useLocation();

  const [widgetsReady, setWidgetsReady] = useState(false);
  const isStandalonePage = STANDALONE_ROUTES.some(
    ({ path }) => path === location.pathname,
  );

  useEffect(() => {
    const timer = setTimeout(() => setWidgetsReady(true), WIDGET_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <Suspense fallback={<Loading />}>
          <ScrollToTop />

          <Routes>
            {renderRoutes(STANDALONE_ROUTES)}
            {NEWSITE_ROUTES.map((path) => (
              <Route key={path} path={path} element={<NewsiteHome />} />
            ))}
            <Route element={<Layout />}>
              {/* Core site pages */}
              {renderRoutes(CORE_ROUTES)}

              {/* Pathway and qualification overview pages */}
              {renderRoutes(PATHWAY_ROUTES)}

              {/* Individual program pages */}
              {renderRoutes(PROGRAM_ROUTES)}
            </Route>
          </Routes>

          {/* Load non-critical widgets after the main app has rendered */}
          {widgetsReady && !isStandalonePage && (
            <Suspense fallback={null}>
              <FuturisticChatbot />
              <FloatingScrollToTop />
            </Suspense>
          )}
        </Suspense>
      </div>
    </HelmetProvider>
  );
}

export default App;
